import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLang } from '../context/LanguageContext';

export default function CookieConsent() {
  const { t, lang } = useLang();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice with TTL
    const consentData = localStorage.getItem('cookieConsent');
    
    if (consentData) {
      try {
        const { timestamp } = JSON.parse(consentData);
        const now = Date.now();
        const ttl = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        // Check if cookie consent is still valid (within 24 hours)
        if (now - timestamp < ttl) {
          // Cookie consent is still valid, don't show banner
          return;
        } else {
          // Cookie consent expired, remove it
          localStorage.removeItem('cookieConsent');
        }
      } catch (e) {
        // If data is corrupted, remove it
        localStorage.removeItem('cookieConsent');
      }
    }
    
    // Show banner after a short delay if no valid consent found
    setTimeout(() => setIsVisible(true), 1000);
  }, []);

  const handleAccept = () => {
    const consentData = {
      consent: 'accepted',
      timestamp: Date.now()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleReject = () => {
    const consentData = {
      consent: 'rejected',
      timestamp: Date.now()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop - затемнение фона */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9998]"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Cookie Banner */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-2xl w-full">
              <div 
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(10,11,15,0.98) 0%, rgba(20,21,25,0.98) 100%)',
                  border: '2px solid rgba(212,175,55,0.4)',
                  boxShadow: '0 30px 80px rgba(0,0,0,0.8), inset 0 1px 0 rgba(212,175,55,0.3)'
                }}
              >
                {/* Background glow */}
                <div
                  className="orb-gold absolute"
                  style={{ width: 400, height: 400, top: '-30%', right: '-20%', opacity: 0.15, pointerEvents: 'none' }}
                />
                
                <div className="relative z-10">
                  {/* Icon + Title */}
                  <div className="flex items-center gap-4 mb-6">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ 
                        background: 'linear-gradient(135deg, rgba(212,175,55,0.3) 0%, rgba(212,175,55,0.15) 100%)',
                        border: '1px solid rgba(212,175,55,0.5)'
                      }}
                    >
                      <Cookie size={28} className="text-[#D4AF37]" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-white font-heading">
                      Cookies
                    </h3>
                  </div>

                  {/* Content */}
                  <div className="mb-6">
                    <p className="text-[#E5E7EB] text-base leading-relaxed mb-4">
                      {t.cookies.banner_text}
                    </p>
                    <Link 
                      to={`/${lang}/cookies-policy`}
                      onClick={() => setIsVisible(false)}
                      className="text-[#D4AF37] hover:text-[#FFD700] text-sm font-semibold underline transition-colors inline-flex items-center gap-1"
                    >
                      {t.cookies.learn_more} →
                    </Link>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleReject}
                      className="flex-1 px-6 py-4 rounded-xl font-bold text-base transition-all duration-300 hover:scale-[1.02] border-2 border-white/20 text-[#BFC3C9] hover:bg-white/10 hover:border-white/30"
                    >
                      {t.cookies.reject}
                    </button>
                    <button
                      onClick={handleAccept}
                      className="flex-1 px-6 py-4 rounded-xl font-bold text-base transition-all duration-300 hover:scale-[1.02]"
                      style={{
                        background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                        color: '#000',
                        boxShadow: '0 8px 30px rgba(212,175,55,0.5)'
                      }}
                    >
                      {t.cookies.accept}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
